import {
    AddOutlined,
    ExpandMore,
    RecentActors,
    ReplayOutlined,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    MenuItem,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../Utilities/AppContext";
import { POST } from "../../Utilities/BaseService";
import { Print } from "../../Utilities/Print";
import { Footer } from "../Header/Footer";
import { Header } from "../Header/Header";
import { getReportTemplates } from "../Payment/Payment";
import { monthFormatter } from "../Reading/Bill/SingleBill";
import { SelectColumns } from "../Table/BaseTable";
import ReportBody from "./Components/ReportBody";
import ReportHeader from "./Components/ReportHeader";
import ReportsFilter from "./Components/ReportsFilter";

type Filter = {
    cashSourceOpts: {};
    dtOpts: {
        start?: string;
        end?: string;
        reference?: string;
    };
    customerFilter: {
        district?: number | number[];
        village?: number | number[];
        hamlet?: number | number[];
        meterNo?: string;
    };
    pagination: {
        page?: number;
        limit?: number;
    };
};

type ReportDetails = {
    date?: any;
    lineRent?: string;
    connectionCharges?: string;
    units?: string;
    amountInDueDate?: string;
    amountAfterDueDate?: string;
    additionalCharges?: string;
    totalSurcharge?: string;
    payedSurcharge?: string;
    remainingAmount?: string;
    paidAmount?: string;
};
type CustomerDetails = any;

type Reading = {
    [key: number]: {
        date: string;
        lineRent: number;
        totalSurcharge: number;
        payedSurcharge: number;
        units: number;
        amountInDueDate: number;
        amountAfterDueDate: number;
        connectionCharges: number;
        additionalCharges: number;
        remainingAmount: number;
        paidAmount: number;
    }[];
};

const getCustomerReport = ({
    cashSourceOpts,
    dtOpts,
    customerFilter,
    pagination,
}: any) => {
    return POST("/reports/customer-report", {
        cashSourceOpts,
        dtOpts,
        customerFilter,
        pagination,
    });
};

const addReportTemplate = (template: {
    key: string;
    value: string;
    user: number;
    category: string;
}) => POST("/preferences", template);

const initialReportColumns = [
    "lineRent",
    "units",
    "additionalCharges",
    "amountInDueDate",
    "amountAfterDueDate",
    "paidAmount",
    "remainingAmount",
];

function calculateSurcharge(amount: number, surcharge: any) {
    try {
        if (surcharge.isSurchargePct) {
            return amount * (surcharge.value / 100);
        } else {
            return surcharge.value;
        }
    } catch (err) {
        return 0;
    }
}

// const getRemainingAmount = (reading: any, _referenceDate: any, row: any) => {
//     let totalAmount = reading.totalAmount;
//     let remainingAmount = 0;

//     const referenceDate = _referenceDate
//         ? Date.parse(_referenceDate)
//         : new Date().getTime();
//     const collecttedAdditionaCharges = reading.additionalCharges
//         .flatMap((item: any) => item.collections)
//         .reduce(
//             (sum: number, curr: any) => sum + curr.amount + curr.adjustment,
//             0
//         );

//     const collectedAmount =
//         reading.collections.reduce(
//             (prev: any, curr: any) => curr.amount + curr.adjustment + prev,
//             0
//         ) +
//         reading.surchargeAdj +
//         reading.payedSurcharge;

//     const sortedCollections: any[] = reading.collections.sort(
//         (a: any, b: any) =>
//             new Date(a.collectionDate).getTime() -
//             new Date(b.collectionDate).getTime()
//     );

//     const isCollectionMade = Boolean(sortedCollections.length);
//     const isFirstCollectionAfterDueDate = isCollectionMade
//         ? new Date(sortedCollections[0].collectionDate) >
//           new Date(reading.dueDate)
//         : false;

//     if (!isCollectionMade) {
//         const surchargeApplies =
//             new Date(reading.dueDate).getTime() < referenceDate;
//         if (surchargeApplies) {
//             totalAmount += calculateSurcharge(
//                 reading.totalAmount,
//                 row.priceType.surcharge
//             );
//         }
//     } else if (isFirstCollectionAfterDueDate) {
//         totalAmount += reading.applicableSurcharge;
//     }

//     remainingAmount =
//         totalAmount - collectedAmount - collecttedAdditionaCharges;

//     return remainingAmount;
// };

const getRemainingAmount = (reading: any, _referenceDate: any, row: any) => {
    let totalAmount = Number(reading.totalAmount) || 0;
    let remainingAmount = 0;

    const referenceDate = _referenceDate
        ? Date.parse(_referenceDate) || new Date().getTime()
        : new Date().getTime();

    const collecttedAdditionaCharges = (reading.additionalCharges || [])
        .flatMap((item: any) => item.collections || [])
        .reduce(
            (sum: number, curr: any) =>
                sum +
                (Number(curr.amount) || 0) +
                (Number(curr.adjustment) || 0),
            0
        );

    const collectedAmount =
        (reading.collections || []).reduce(
            (prev: number, curr: any) =>
                prev +
                (Number(curr.amount) || 0) +
                (Number(curr.adjustment) || 0),
            0
        ) +
        (Number(reading.surchargeAdj) || 0) +
        (Number(reading.payedSurcharge) || 0);

    const sortedCollections: any[] = (reading.collections || []).sort(
        (a: any, b: any) =>
            (new Date(a.collectionDate).getTime() || 0) -
            (new Date(b.collectionDate).getTime() || 0)
    );

    const isCollectionMade = Boolean(sortedCollections.length);
    const isFirstCollectionAfterDueDate = isCollectionMade
        ? (new Date(sortedCollections[0].collectionDate).getTime() || 0) >
          (new Date(reading.dueDate).getTime() || 0)
        : false;

    if (!isCollectionMade) {
        const surchargeApplies =
            (new Date(reading.dueDate).getTime() || 0) < referenceDate;
        if (surchargeApplies) {
            totalAmount += calculateSurcharge(
                totalAmount,
                row.priceType?.surcharge || 0
            );
        }
    } else if (isFirstCollectionAfterDueDate) {
        totalAmount += Number(reading.applicableSurcharge) || 0;
    }

    remainingAmount =
        reading.wrongReadingFlag == true
            ? 0
            : totalAmount - collectedAmount - collecttedAdditionaCharges;

    return remainingAmount;
};

const CustomerReport = () => {
    const [expanded, setExpanded] = useState(true);
    const [printMode, setPrintMode] = useState(false);
    const [openAddReportTemplate, setOpenAddReportTemplate] = useState(false);
    const qc = useQueryClient();

    const [filter, setFilter] = useState<Filter>({
        customerFilter: {},
        cashSourceOpts: {},
        dtOpts: { reference: new Date().toISOString().split("T")[0] },
        pagination: { page: 0, limit: 10 },
    });
    const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
        name: "Name",
        cnic: "CNIC",
        meterNo: "Meter No.",
        contact: "Contact",
        property: "Property",
        fatherName: "Father name",
        propertyAddress: "Property Address",
    });
    const [selectedCustomerDetails, setSelectedCustomerDetails] = useState([
        "name",
        "meterNo",
    ]);
    const [reportDetails, setReportDetails] = useState<ReportDetails>({
        lineRent: "Line Rent",
        units: "Units",
        additionalCharges: "Additional Charges",
        amountInDueDate: "Amount Within Due Date",
        amountAfterDueDate: "Amount After Due Date",
        paidAmount: "Paid Amount",
        remainingAmount: "Remaining Amount",
        totalSurcharge: "Total Surcharge",
        payedSurcharge: "Paid Surcharge",
        connectionCharges: "Connection Charges",
    });
    const [selectedReportDetails, setSelectedReportDetails] =
        useState(initialReportColumns);
    const [newReportTemplate, setNewReportTemplate] = useState({
        key: "",
        value: [],
    });
    const [reportTemplateFeedback, setReportTemplateFeedback] = useState({
        show: false,
        loading: false,
        message: "",
        status: "",
    });
    const [selectedReportTemplate, setSelectedReportTemplate] = useState<{
        name: string;
        columns: string[];
    }>({
        name: "",
        columns: [],
    });

    const [readings, setReadings] = useState<Reading>();

    const theme = useTheme();
    const printRef = useRef<HTMLDivElement | null>(null);
    const { user } = useContext(AppContext);

    const queryClient = useQueryClient();

    const handleCustomerReportSuccess = (res: any) => {
        const reports: any[] = res.data.rows;

        // if (reports.length > 0) {
        //     const targetReadings = reports.reduce(
        //         (prev: any, curr: any) => ({
        //             ...prev,
        //             [curr.id]: curr.readings.map((reading: any) => ({
        //                 date: monthFormatter.format(
        //                     new Date(reading.year, reading.month - 1)
        //                 ),
        //                 lineRent: reading.lineRent,

        //                 totalSurcharge: reading.applicableSurcharge,

        //                 payedSurcharge:
        //                     reading.payedSurcharge + reading.surchargeAdj,

        //                 units: reading.currentUnits - reading.prevUnits,

        //                 collections: reading.collections,

        //                 amountInDueDate: reading.totalAmount,

        //                 amountAfterDueDate:
        //                     reading.totalAmount + reading.applicableSurcharge,

        //                 connectionCharges: reading.connectionChargesIncluded
        //                     ? (curr.connectionCharges ?? 0)
        //                     : 0,

        //                 additionalCharges: reading.additionalCharges.reduce(
        //                     (acc: any, curr: any) => acc + curr.amount,
        //                     0
        //                 ),
        //                 additionChargesData: reading.additionalCharges.flatMap(
        //                     (charge: any) =>
        //                         (charge.collections ?? [])
        //                             .map((collection: any) => {
        //                                 if (!collection) return null;

        //                                 return {
        //                                     amount: collection.amount ?? 0,
        //                                     adjustment:
        //                                         collection.adjustment ?? 0,
        //                                     createdAt: collection.createdAt,
        //                                 };
        //                             })
        //                             .filter(Boolean)
        //                 ),

        //                 remainingAmount: getRemainingAmount(
        //                     reading,
        //                     filter.dtOpts.reference,
        //                     curr
        //                 ),

        //                 paidAmount:
        //                     reading.totalAmount +
        //                     reading.payedSurcharge +
        //                     reading.surchargeAdj -
        //                     (reading.totalAmount -
        //                         reading.collections
        //                             .map((r: any) => r.amount + r.adjustment)
        //                             .reduce(
        //                                 (prev: any, curr: any) => prev + curr,
        //                                 0
        //                             )) +
        //                     reading.additionalCharges
        //                         .flatMap((item: any) => item.collections)
        //                         .reduce(
        //                             (sum: number, curr: any) =>
        //                                 sum + curr.amount + curr.adjustment,
        //                             0
        //                         ),
        //             })),
        //         }),
        //         {}
        //     );

        //     setReadings(targetReadings);
        // }
        if (reports.length > 0) {
            const toNumber = (val: any) => Number(val ?? 0);

            const targetReadings = reports.reduce(
                (prev: any, curr: any) => ({
                    ...prev,
                    [curr.id]: (curr.readings ?? []).map((reading: any) => {
                        const collectionTotal = (
                            reading.collections ?? []
                        ).reduce(
                            (sum: number, r: any) =>
                                sum +
                                toNumber(r?.amount) +
                                toNumber(r?.adjustment),
                            0
                        );

                        const additionalCollectionTotal = (
                            reading.additionalCharges ?? []
                        )
                            .flatMap((item: any) => item?.collections ?? [])
                            .reduce(
                                (sum: number, c: any) =>
                                    sum +
                                    toNumber(c?.amount) +
                                    toNumber(c?.adjustment),
                                0
                            );

                        return {
                            date: monthFormatter.format(
                                new Date(reading.year, reading.month - 1)
                            ),

                            lineRent: toNumber(reading.lineRent),

                            totalSurcharge: toNumber(
                                reading.applicableSurcharge
                            ),

                            payedSurcharge:
                                toNumber(reading.payedSurcharge) +
                                toNumber(reading.surchargeAdj),

                            units: reading.consumedUnits,

                            collections: reading.collections ?? [],

                            amountInDueDate: toNumber(reading.totalAmount),

                            amountAfterDueDate:
                                toNumber(reading.totalAmount) +
                                toNumber(reading.applicableSurcharge),

                            connectionCharges: reading.connectionChargesIncluded
                                ? toNumber(curr.connectionCharges)
                                : 0,

                            additionalCharges: (
                                reading.additionalCharges ?? []
                            ).reduce(
                                (acc: number, charge: any) =>
                                    acc + toNumber(charge?.amount),
                                0
                            ),

                            additionChargesData: (
                                reading.additionalCharges ?? []
                            ).flatMap((charge: any) =>
                                (charge?.collections ?? [])
                                    .map((collection: any) => {
                                        if (!collection) return null;

                                        return {
                                            amount: toNumber(
                                                collection?.amount
                                            ),
                                            adjustment: toNumber(
                                                collection?.adjustment
                                            ),
                                            createdAt: collection?.createdAt,
                                        };
                                    })
                                    .filter(Boolean)
                            ),

                            remainingAmount: getRemainingAmount(
                                reading,
                                filter.dtOpts.reference,
                                curr
                            ),

                            paidAmount:
                                toNumber(reading.totalAmount) +
                                toNumber(reading.payedSurcharge) +
                                toNumber(reading.surchargeAdj) -
                                (toNumber(reading.totalAmount) -
                                    collectionTotal) +
                                additionalCollectionTotal,
                        };
                    }),
                }),
                {}
            );

            setReadings(targetReadings);
        }
    };

    const { data, isFetching, isSuccess } = useQuery(
        ["customer-report", filter],
        () => getCustomerReport(filter),
        {
            enabled: Boolean(filter?.customerFilter?.meterNo),
            onSuccess: handleCustomerReportSuccess,
        }
    );

    const { data: reportTemplates } = useQuery(["report-templates"], () =>
        getReportTemplates(user.id, "Customer Report")
    );

    const handleAddReportTemplate = () => {
        setReportTemplateFeedback({
            ...reportTemplateFeedback,
            loading: true,
        });
        reportTemplateMutation.mutate({
            ...newReportTemplate,
            value: JSON.stringify(newReportTemplate.value),
            user: user.id,
            category: "Customer Report",
        });
    };

    const reportTemplateMutation = useMutation(addReportTemplate, {
        onSuccess(res) {
            setReportTemplateFeedback({
                show: true,
                loading: false,
                message: res.data.message,
                status: "success",
            });
        },

        onError(err) {
            setReportTemplateFeedback({
                show: true,
                loading: false,
                message: err as string,
                status: "error",
            });
        },
    });

    useEffect(() => {
        if (selectedReportTemplate?.name) {
            console.log(selectedReportTemplate.columns);
            setSelectedReportDetails(selectedReportTemplate.columns);
        } else {
            setSelectedReportDetails(initialReportColumns);
        }
    }, [selectedReportTemplate]);

    return (
        <Box>
            <Accordion
                variant="outlined"
                elevation={0}
                sx={{ bgcolor: theme.palette.common.white }}
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h5" color="GrayText">
                        Consumer Summary
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <ReportsFilter filter={filter} setFilter={setFilter} />
                </AccordionDetails>
            </Accordion>
            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
            >
                {!Boolean(filter?.customerFilter?.meterNo) && (
                    <Grid item xs={12}>
                        <Alert color="info" severity="info">
                            Please enter a meter number to generate customer
                            report.
                        </Alert>
                    </Grid>
                )}

                {!Boolean(data?.data.rows.length) && isSuccess && (
                    <Grid item xs={12}>
                        <Alert color="info" severity="info">
                            Found 0 records.
                        </Alert>
                    </Grid>
                )}

                {/* <Grid item xs={12} my={2}>
                    <TextField
                        label="Reference Date"
                        type="date"
                        size="small"
                        name="reference"
                        value={filter.dtOpts.reference}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                            setFilter({
                                ...filter,
                                dtOpts: {
                                    ...filter.dtOpts,
                                    reference: e.target.value,
                                },
                            })
                        }
                        fullWidth
                    />
                </Grid> */}

                <Grid item>
                    <Print
                        componentRef={printRef}
                        setPrintMode={setPrintMode}
                    />

                    <SelectColumns
                        columns={reportDetails}
                        selectedColumns={selectedReportDetails}
                        setColumns={setSelectedReportDetails}
                    />

                    <SelectColumns
                        columns={customerDetails}
                        selectedColumns={selectedCustomerDetails}
                        setColumns={setSelectedCustomerDetails}
                        label="Select Customer Details"
                        icon={<RecentActors fontSize="small" />}
                    />

                    <Tooltip title="Reload table">
                        <IconButton
                            size="small"
                            onClick={() => qc.resetQueries(["customer-report"])}
                        >
                            <ReplayOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid
                    item
                    container
                    flex={1}
                    justifyContent="center"
                    spacing={1}
                >
                    {reportTemplates?.data.rows.length > 0 &&
                        reportTemplates?.data.rows.map(
                            (report: any, index: number) => (
                                <Grid item key={index}>
                                    <Chip
                                        variant={
                                            selectedReportTemplate.name ===
                                            report.key
                                                ? "filled"
                                                : "outlined"
                                        }
                                        color="secondary"
                                        onDelete={
                                            selectedReportTemplate.name ===
                                            report.key
                                                ? () =>
                                                      setSelectedReportTemplate(
                                                          {
                                                              name: "",
                                                              columns: [],
                                                          }
                                                      )
                                                : undefined
                                        }
                                        size="small"
                                        label={report.key}
                                        onClick={() =>
                                            setSelectedReportTemplate({
                                                name: report.key,
                                                columns: JSON.parse(
                                                    report.value
                                                ),
                                            })
                                        }
                                    />
                                </Grid>
                            )
                        )}

                    <Grid item>
                        <IconButton
                            size="small"
                            onClick={() => setOpenAddReportTemplate(true)}
                            title="Add new report template"
                        >
                            <AddOutlined fontSize="small" />
                        </IconButton>

                        <Dialog
                            open={openAddReportTemplate}
                            onClose={() => {
                                setOpenAddReportTemplate(false);
                                setNewReportTemplate({
                                    key: "",
                                    value: [],
                                });
                                setReportTemplateFeedback({
                                    ...reportTemplateFeedback,
                                    show: false,
                                    message: "",
                                });

                                queryClient.invalidateQueries([
                                    "report-templates",
                                ]);
                            }}
                            fullWidth
                        >
                            <DialogTitle>Add Report Template</DialogTitle>

                            <DialogContent>
                                <Grid container spacing={1}>
                                    {reportTemplateFeedback.show && (
                                        <Grid item xs={12} mb={2}>
                                            <Alert
                                                severity={
                                                    reportTemplateFeedback.status as any
                                                }
                                            >
                                                {reportTemplateFeedback.message}
                                            </Alert>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} mt={1}>
                                        <TextField
                                            label="Report Name"
                                            size="small"
                                            fullWidth
                                            value={newReportTemplate.key}
                                            onChange={(e) =>
                                                setNewReportTemplate({
                                                    ...newReportTemplate,
                                                    key: e.target.value,
                                                })
                                            }
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            required
                                            SelectProps={{ multiple: true }}
                                            size="small"
                                            fullWidth
                                            label="Columns"
                                            value={newReportTemplate.value}
                                            onChange={(e) =>
                                                setNewReportTemplate({
                                                    ...newReportTemplate,
                                                    value: e.target
                                                        .value as any,
                                                })
                                            }
                                        >
                                            {Object.entries(reportDetails).map(
                                                ([k, v]: any[]) => (
                                                    <MenuItem key={k} value={k}>
                                                        {v}
                                                    </MenuItem>
                                                )
                                            )}
                                        </TextField>
                                    </Grid>

                                    <Grid item mt={1}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            disabled={
                                                newReportTemplate.key.length ==
                                                    0 ||
                                                newReportTemplate.value
                                                    .length == 0 ||
                                                reportTemplateFeedback.loading
                                            }
                                            onClick={handleAddReportTemplate}
                                        >
                                            {reportTemplateFeedback.loading
                                                ? "saving "
                                                : "save "}
                                            template
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Dialog>
                    </Grid>
                </Grid>

                {/* <Grid item>
                    <BlindPagination
                        pagination={filter.pagination as any}
                        setPagination={(value) =>
                            setFilter({ ...filter, pagination: value }) as any
                        }
                    />
                </Grid> */}
            </Grid>
            {isFetching && <LinearProgress sx={{ my: 1 }} />}

            <TableContainer
                ref={printRef}
                sx={{ p: printMode ? 2 : undefined }}
            >
                {data?.data.rows.map((report: any, idx: number) => (
                    <Fragment key={report.id}>
                        {readings && report && (
                            <Table
                                key={report.id}
                                padding="none"
                                sx={{ mb: 5 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            colSpan={
                                                selectedReportDetails.length + 1
                                            }
                                        >
                                            {printMode && (
                                                <Header
                                                    name={
                                                        selectedReportTemplate.name ||
                                                        "Customer Report"
                                                    }
                                                />
                                            )}

                                            <ReportHeader
                                                printMode={printMode}
                                                customerData={{
                                                    meterNo: report.meterNo,
                                                    name: report.property
                                                        .customer.name,
                                                    fatherName:
                                                        report.property.customer
                                                            .fatherName,
                                                    cnic: report.property
                                                        .customer.cnic,
                                                    contact:
                                                        report.property.customer
                                                            .mobileNo,
                                                    property:
                                                        report.property.name,
                                                    propertyAddress:
                                                        report.property.address,
                                                }}
                                                customerDetails={
                                                    customerDetails
                                                }
                                                selectedCustomerDetails={
                                                    selectedCustomerDetails
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                px: 1,
                                                py: 2,
                                                fontWeight: "bolder",
                                            }}
                                        >
                                            Month
                                        </TableCell>
                                        {selectedReportDetails.map(
                                            (item: any, idx) => (
                                                <TableCell
                                                    key={idx}
                                                    sx={{
                                                        px: 1,
                                                        py: 2,
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    {
                                                        reportDetails[
                                                            item as keyof ReportDetails
                                                        ]
                                                    }
                                                </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                </TableHead>

                                <ReportBody
                                    reportData={readings[report.id]}
                                    reportDetails={reportDetails}
                                    selectedReportDetails={
                                        selectedReportDetails
                                    }
                                    calculationStrategy={JSON.parse(
                                        report.priceType.surcharge
                                            .calculationStrategy
                                    )}
                                    printMode={printMode}
                                />

                                {printMode && (
                                    <Footer
                                        span={selectedReportDetails.length + 1}
                                    />
                                )}
                            </Table>
                        )}
                    </Fragment>
                ))}
            </TableContainer>
        </Box>
    );
};

export default CustomerReport;
